/**
 * Header JavaScript - Optimized for performance
 */

class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.header = document.querySelector('.section-header');
    this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always' || this.getAttribute('data-sticky-type') === 'reduce-logo-size';
    this.headerBounds = {};
    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.setHeaderHeight();
    this.setupEventListeners();
    this.createObserver();
    this.initMobileMenuSliders();
  }

  setHeaderHeight() {
    if (this.header) {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }
  }

  setupEventListeners() {
    // Add event listeners for hover on desktop
    if (window.innerWidth >= 990) {
      this.setupDesktopMenuHover();
    }

    window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => this.preventReveal = true;

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
  }

  setupDesktopMenuHover() {
    const header = document.querySelector('.header');
    const megaMenus = document.querySelectorAll('.mega-menu');
    const dropdownMenus = document.querySelectorAll('.header__submenu');
    
    megaMenus.forEach(menu => {
      menu.addEventListener('mouseenter', () => {
        menu.setAttribute('open', '');
        if (header) header.classList.add('menu-hover-active');
      });
      menu.addEventListener('mouseleave', () => {
        menu.removeAttribute('open');
        if (header) header.classList.remove('menu-hover-active');
      });
    });

    dropdownMenus.forEach(menu => {
      const parentItem = menu.closest('.list-menu__item');
      if (parentItem) {
        parentItem.addEventListener('mouseenter', () => {
          const details = parentItem.querySelector('details');
          if (details) details.setAttribute('open', '');
          if (header) header.classList.add('menu-hover-active');
        });
        parentItem.addEventListener('mouseleave', () => {
          const details = parentItem.querySelector('details');
          if (details) details.removeAttribute('open');
          if (header) header.classList.remove('menu-hover-active');
        });
      }
    });
  }

  createObserver() {
    const observer = new IntersectionObserver((entries) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);
        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);
        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    this.closeMenuDisclosure();
    this.closeSearchModal();
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach(disclosure => disclosure.close());
  }

  closeSearchModal() {
    this.searchModal = this.searchModal || this.header.querySelector('details-modal');
    if (this.searchModal) this.searchModal.close(false);
  }

  initMobileMenuSliders() {
    const sliders = document.querySelectorAll('.mobile-menu__slider');
    
    sliders.forEach(slider => {
      const sliderId = slider.dataset.sliderId;
      const dots = document.querySelector(`[data-dots-for="${sliderId}"]`);
      const scrollButtons = document.querySelectorAll(`[data-slider="${sliderId}"]`);
      
      if (dots) {
        this.setupSliderDots(slider, dots);
      }
      
      if (scrollButtons.length > 0) {
        this.setupSliderScrollButtons(slider, scrollButtons);
      }
    });
  }

  setupSliderDots(slider, dots) {
    const dotButtons = dots.querySelectorAll('.mobile-menu__slider-dot');
    
    dotButtons.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        const itemWidth = parseInt(slider.style.getPropertyValue('--slider-item-width')) || 200;
        const scrollPosition = index * (itemWidth + 16); // 16px is the gap
        slider.scrollTo({ left: scrollPosition, behavior: 'smooth' });
        
        // Update active dot
        dotButtons.forEach(d => d.classList.remove('active'));
        dot.classList.add('active');
      });
    });
    
    // Update dots on scroll
    slider.addEventListener('scroll', () => {
      const itemWidth = parseInt(slider.style.getPropertyValue('--slider-item-width')) || 200;
      const currentIndex = Math.round(slider.scrollLeft / (itemWidth + 16));
      
      dotButtons.forEach((dot, index) => {
        dot.classList.toggle('active', index === currentIndex);
      });
    });
  }

  setupSliderScrollButtons(slider, buttons) {
    buttons.forEach(button => {
      button.addEventListener('click', () => {
        const direction = button.dataset.direction;
        const itemWidth = parseInt(slider.style.getPropertyValue('--slider-item-width')) || 200;
        const scrollAmount = itemWidth + 16; // 16px is the gap
        
        if (direction === 'left') {
          slider.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        } else {
          slider.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
      });
    });
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
  }
}

// Register custom element
customElements.define('sticky-header', StickyHeader); 